import { BoardID, createBoardID } from 'BoardID';
import {
  changePatternMaterial,
  changePatternSize,
  createRandomMaterialPatternString,
  createRandomSubBoardPatternString,
  getPatternStringSize,
  MaterialPatternString,
  parseMaterialPatternString,
  parseSubBoardPatternString,
  scalePatternString,
} from 'Pattern';
import {
  BoardDetails,
  BoardType,
  Dimensions2D,
  Dimensions3D,
  Direction,
  Material,
  ParamsPanelSelection,
  Renderable,
  RenderableMetadata,
  Settings,
  StripsAdvancedParams,
  TwoArray,
  Units,
} from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import Strips from 'boardClasses/strips/Strips';
import {
  getSubBoardThicknessesByKey,
  subpatternIndexToKey,
  subpatternKeyToIndex,
} from 'utils/patternUtils';
import {
  fillRenderablesAt,
  flippedIndex,
  sample,
  scaleNumber,
  sortBy,
} from 'utils/utils';

export default class StripsAdvanced extends BoardBase<BoardType.StripsAdvanced> {
  constructor(
    id: BoardID,
    name: string,
    params: StripsAdvancedParams,
    units?: Units
  ) {
    super(BoardType.StripsAdvanced, id, name, params, units);
  }

  static createRandom(
    materials: TwoArray<Material>,
    size: Dimensions2D
  ): StripsAdvancedParams {
    const numSubpatterns = sample([2, 3, 4]);
    const subpatterns = new Array(numSubpatterns)
      .fill(null)
      .map((_) =>
        createRandomMaterialPatternString(1 / 4, 2, size.width, materials)
      ) as TwoArray<MaterialPatternString>;
    const subpatternKeys = subpatterns.map((_, index) =>
      subpatternIndexToKey(index)
    ) as TwoArray<string>;

    return {
      pattern: createRandomSubBoardPatternString(
        1 / 4,
        2,
        size.height,
        subpatternKeys
      ),
      subpatterns,
      thickness: 1,
    };
  }

  getRenderables(): Array<Renderable<BoardType.StripsAdvanced>> {
    const pattern = parseSubBoardPatternString(this.params.pattern);
    const subpatterns = this.params.subpatterns.map(parseMaterialPatternString);
    const subpatternsReversed = subpatterns.map((subpattern) =>
      [...subpattern].reverse()
    );

    const renderables: Array<Renderable<BoardType.StripsAdvanced>> = [];

    let topOffset = 0;

    for (let rowIndex = 0; rowIndex < pattern.length; ++rowIndex) {
      const { size, key, flip } = pattern[rowIndex];

      const subpattern =
        key &&
        (flip ? subpatternsReversed : subpatterns)[subpatternKeyToIndex(key)];

      if (key && subpattern) {
        fillRenderablesAt(
          renderables,
          subpattern,
          topOffset,
          0,
          size,
          Direction.H,
          ({ fragmentIndex, renderableIndex }) => ({
            type: BoardType.StripsAdvanced,
            subpatternIndex: subpatternKeyToIndex(key),
            fragmentIndex: flippedIndex(subpattern.length, fragmentIndex, flip),
            rowIndex,
            renderableIndex,
          })
        );
      }

      topOffset += size;
    }

    return renderables;
  }

  getIsRenderableHighlighted(
    renderable: Renderable<BoardType.StripsAdvanced>,
    selection: Renderable<BoardType.StripsAdvanced>
  ): boolean {
    return (
      renderable.metadata.fragmentIndex === selection.metadata.fragmentIndex &&
      renderable.metadata.subpatternIndex === selection.metadata.subpatternIndex
    );
  }

  getBoardDetails(settings: Settings): BoardDetails {
    const { subpatterns, thickness } = this.params;
    const pattern = parseSubBoardPatternString(this.params.pattern);

    const subBoardThicknessesByKey = getSubBoardThicknessesByKey(pattern);

    let idealStartingStock: Array<{
      label?: string;
      stock: Array<{ material: Material | null; dimensions: Dimensions3D }>;
    }> = [
      {
        stock: [],
      },
    ];

    for (const subpatternKey in subBoardThicknessesByKey) {
      const sub: {
        label?: string;
        stock: Array<{ material: Material | null; dimensions: Dimensions3D }>;
      } = {
        label: 'Subpattern ' + subpatternKey,
        stock: [],
      };

      idealStartingStock.push(sub);

      for (const size in subBoardThicknessesByKey[subpatternKey]) {
        const length = subBoardThicknessesByKey[subpatternKey][size];
        const subpattern = subpatterns[subpatternKeyToIndex(subpatternKey)];

        if (subpattern == null) {
          continue;
        }

        const subBoardDetails = new Strips(
          createBoardID(),
          '',
          {
            numRows: length / +size,
            rowHeight: +size,
            thickness,
            pattern: subpattern,
            flip: false,
          },
          Units.IN
        ).getBoardDetails(settings);

        if (subBoardDetails == null) {
          continue;
        }

        sub.stock = [
          ...sub.stock,
          ...subBoardDetails.idealStartingStock[0].stock,
        ];
      }
    }

    return {
      idealStartingStock: sortBy(idealStartingStock, ['label']),
    };
  }

  handleSelectedSizeChange(
    { metadata }: Renderable<BoardType.StripsAdvanced>,
    size: number
  ) {
    return this.updateParams({
      subpatterns: this.params.subpatterns.map((subpattern, subpatternIndex) =>
        metadata.subpatternIndex === subpatternIndex
          ? changePatternSize(subpattern, metadata.fragmentIndex, size)
          : subpattern
      ),
    });
  }

  handleSelectedMaterialChange(
    { metadata }: Renderable<BoardType.StripsAdvanced>,
    material: Material
  ) {
    return this.updateParams({
      subpatterns: this.params.subpatterns.map((subpattern, subpatternIndex) =>
        metadata.subpatternIndex === subpatternIndex
          ? changePatternMaterial(subpattern, metadata.fragmentIndex, material)
          : subpattern
      ),
    });
  }

  previewSelectionToParamsPanelSelection(
    selection: RenderableMetadata<BoardType.StripsAdvanced>
  ): ParamsPanelSelection<BoardType.StripsAdvanced> {
    return {
      renderableIndex: selection.fragmentIndex,
      subpatternIndex: selection.subpatternIndex,
    };
  }

  paramsPanelSelectionToPreviewSelection(
    selection: ParamsPanelSelection<BoardType.StripsAdvanced>
  ): null | RenderableMetadata<BoardType.StripsAdvanced> {
    if (selection == null) {
      return null;
    }

    const pattern = parseSubBoardPatternString(this.params.pattern);
    // Find first renderable index in the preview that uses this suboattern
    let renderableIndex = 0;
    for (const { key } of pattern) {
      if (key == null) {
        continue;
      }

      const subpatternIndex = subpatternKeyToIndex(key);
      if (subpatternIndex !== selection.subpatternIndex) {
        renderableIndex += getPatternStringSize(
          this.params.subpatterns[subpatternIndex]
        );
      } else {
        renderableIndex += selection.renderableIndex;
        break;
      }
    }

    return {
      renderableIndex,
      fragmentIndex: selection.renderableIndex,
      subpatternIndex: selection.subpatternIndex,
      rowIndex: selection.subpatternIndex,
    };
  }

  scaleParams(
    params: StripsAdvancedParams,
    scaleFactor: number
  ): StripsAdvancedParams {
    return {
      ...params,
      thickness: scaleNumber(params.thickness, scaleFactor),
      pattern: scalePatternString(params.pattern, scaleFactor),
      subpatterns: params.subpatterns.map((subpattern) =>
        scalePatternString(subpattern, scaleFactor)
      ),
    };
  }
}
